import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { DialogContent } from '@material-ui/core';
import store, { adminUserActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { AdminApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { LoadPanel } from 'devextreme-react/load-panel';
import { hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { PhoneInput, isPhoneNumberValid } from '@apply/ui';
import { RefreshEvents } from '@apply/ui';
import { TabPanel, Item } from 'devextreme-react/tab-panel';
import UserAgreementsTable from '../tables/UserAgreementsTable';
import { AdminUserPodTypeOptions } from '../common/constants/AdminUserPodType';

const AdminUserModal = () => {
    const { isOpen, id = 0 } = useSelector((state) => state.adminUsers.adminUserModal);
    const { dispatch } = store;
    const adminApi = new AdminApi.AdminApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [firstName, set_firstName] = useState('');
    const [middleName, set_middleName] = useState('');
    const [lastName, set_lastName] = useState('');
    const [suffix, set_suffix] = useState('');
    const [email, set_email] = useState('');
    const [homePhone, set_homePhone] = useState('');
    const [workPhone, set_workPhone] = useState('');
    const [mobilePhone, set_mobilePhone] = useState('');
    const [title, set_title] = useState('');
    const [isLocked, set_isLocked] = useState(false);
    const [podId, set_podId] = useState('');
    const [isDeleted, set_isDeleted] = useState(false);

    //Dialogs
    const [loadingMessage, set_loadingMessage] = useState('');

    const tabNames = ['Details', 'User Agreements'];

    const handleClose = () =>
        dispatch(
            adminUserActions.setAdminUserModal({
                adminUserModal: { isOpen: false },
            })
        );

    const styles = {
        headerRight: {
            width: '100px',
            position: 'absolute',
            right: '20px',
            paddingTop: '15px',
        },
        buttonContainer: {
            margin: ' 0 20px 0 0',
            textAlign: 'right',
        },
        locked: {
            backgroundColor: '#ff000036',
        },
        unlocked: {
            backgroundColor: '#00800036',
        },
        neutral: {
            backgroundColor: 'white',
        },
        alert: {
            color: 'darkred',
        },
    };

    const setState = (profile) => {
        set_firstName(profile.firstName);
        set_middleName(profile.middleName);
        set_lastName(profile.lastName);
        set_suffix(profile.suffix);
        set_email(profile.email);
        set_homePhone(profile.homePhone);
        set_workPhone(profile.workPhone);
        set_title(profile.title);
        set_mobilePhone(profile.mobilePhone);
        set_isLocked(profile.isLocked);
        set_podId(profile.podId);
        set_isDeleted(profile.isDeleted);
    };

    const resetState = () => {
        set_firstName('');
        set_middleName('');
        set_lastName('');
        set_suffix('');
        set_email('');
        set_homePhone('');
        set_workPhone('');
        set_title('');
        set_mobilePhone('');
        set_isLocked(false);
        set_podId('');
        set_isDeleted(false);
    };

    const loadUserAsync = async (id) => {
        try {
            set_loadingMessage('Loading...');
            const response = await adminApi.getAdminUserProfile(id);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve User Profile!');
            }

            setState(response.result);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const saveAdminUser = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        try {
            set_loadingMessage('Saving...');

            const model = {
                id,
                firstName,
                middleName,
                lastName,
                suffix,
                title,
                email,
                homePhone,
                workPhone,
                mobilePhone,
                isLocked,
                podId,
            };
            const response = await adminApi.updateAdminUserProfile(model);
            if (response.statusCode !== 200) {
                return;
            }

            handleAdminSuccess('Changes saved successfully.');

            refreshEvents.dispatchRefreshEvent();
            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        resetState();
    }, [isOpen]);

    useEffect(() => {
        if (!id) {
            resetState();
            return;
        }

        loadUserAsync(id);
    }, [id]);

    const canEdit = () => {
        return hasPermission(PermissionType.AdminUserEdit) || (hasPermission(PermissionType.AdminUserCreate) && !id);
    };

    const canSave = () => {
        return (
            canEdit() &&
            firstName &&
            lastName &&
            email &&
            title &&
            isPhoneNumberValid(homePhone, false) &&
            isPhoneNumberValid(workPhone, false) &&
            isPhoneNumberValid(mobilePhone, false)
        );
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen ?? false}
            onClose={handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '900px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1
                                    style={{
                                        height: '50px',
                                        marginTop: '15px',
                                    }}
                                >
                                    Admin User Details
                                </h1>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={handleClose}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                            <form
                                name='editAdminUserForm'
                                onSubmit={(event) => saveAdminUser(event)}
                                className={'standard-form'}
                            >
                                <TabPanel>
                                    <Item
                                        title={tabNames[0]}
                                        icon='home'
                                    >
                                        <div
                                            className='scrollable-content'
                                            style={{
                                                padding: '0',
                                                margin: '0',
                                                minHeight: '250px',
                                            }}
                                        >
                                            <div style={{ paddingTop: '10px' }}>
                                                {isDeleted && (
                                                    <div className='row'>
                                                        <div
                                                            className='columns small-7 medium-6 large-12'
                                                            style={styles.alert}
                                                        >
                                                            <FontAwesomeIcon icon={faExclamationTriangle} /> Account is Deleted &
                                                            Locked
                                                        </div>
                                                    </div>
                                                )}

                                                <div className='row'>
                                                    <div className='columns small-7 medium-6 large-4'>
                                                        <label htmlFor='firstName'>
                                                            First Name <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            id='firstName'
                                                            type='text'
                                                            value={firstName}
                                                            maxLength={100}
                                                            onChange={(event) => set_firstName(event.target.value)}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Your first name (e.g. John)</span>
                                                        </span>
                                                    </div>
                                                    <div
                                                        className='columns small-12 large-2'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='middleName'>Middle</label>
                                                        <input
                                                            type='text'
                                                            id='middleName'
                                                            value={middleName}
                                                            selected={middleName}
                                                            onChange={(e) => set_middleName(e.target.value)}
                                                            required={false}
                                                            maxLength={50}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Your middle name or initial</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-7 medium-6 large-4'>
                                                        <label htmlFor='lastName'>
                                                            Last Name <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            id='lastName'
                                                            type='text'
                                                            maxLength={100}
                                                            value={lastName}
                                                            onChange={(event) => set_lastName(event.target.value)}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Your last name (e.g. Smith)</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-12 large-2'>
                                                        <label htmlFor='suffix'>Suffix</label>
                                                        <input
                                                            type='text'
                                                            id='suffix'
                                                            value={suffix}
                                                            selected={suffix}
                                                            placeholder='Jr, III etc'
                                                            onChange={(e) => set_suffix(e.target.value)}
                                                            required={false}
                                                            maxLength={20}
                                                            disabled={!canEdit()}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-7 medium-6 large-6'>
                                                        <label htmlFor='title'>
                                                            Title <span className='req'>*</span>
                                                        </label>
                                                        <input
                                                            id='title'
                                                            type='text'
                                                            maxLength={100}
                                                            value={title}
                                                            onChange={(event) => set_title(event.target.value)}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Your title at BFI</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-7 medium-6 large-3'>
                                                        <label htmlFor='isLocked'>Account Locked?</label>
                                                        <select
                                                            id='isLocked'
                                                            value={isLocked ? '1' : '0'}
                                                            onChange={(e) => set_isLocked(e.target.value === '1', true, false)}
                                                            style={isLocked ? styles.locked : styles.unlocked}
                                                            disabled={
                                                                isDeleted ||
                                                                !canEdit() ||
                                                                !hasPermission(PermissionType.AdminUserLockUnlock)
                                                            }
                                                        >
                                                            <option
                                                                value='0'
                                                                style={styles.neutral}
                                                            >
                                                                No
                                                            </option>
                                                            <option
                                                                value='1'
                                                                style={styles.neutral}
                                                            >
                                                                Yes
                                                            </option>
                                                        </select>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Can the user login?</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-7 medium-6 large-3'>
                                                        <label htmlFor='podId'>Pod</label>
                                                        <select
                                                            id='podId'
                                                            value={podId}
                                                            onChange={(event) => set_podId(event.target.value)}
                                                        >
                                                            <option value=''></option>
                                                            {AdminUserPodTypeOptions.map((r) => {
                                                                return (
                                                                    <option
                                                                        key={r.id}
                                                                        value={r.id}
                                                                    >
                                                                        {r.name}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>
                                                                Pod the user belongs to. Used in reporting.
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-7 medium-6 large-12'>
                                                        <label htmlFor='email'>
                                                            Email <span className='req'>*</span> (also used as Login for the
                                                            account)
                                                        </label>
                                                        <input
                                                            id='email'
                                                            type='email'
                                                            value={email}
                                                            onChange={(event) => set_email(event.target.value)}
                                                            required={true}
                                                            maxLength={320}
                                                            disabled={!canEdit()}
                                                        />
                                                        <div
                                                            style={{
                                                                color: 'darkred',
                                                                fontSize: 'small',
                                                            }}
                                                        >
                                                            * Changing this will also change the login for the account!
                                                        </div>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Your business email address</span>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-7 medium-6 large-3'>
                                                        <label htmlFor='homePhone'>Home Phone</label>
                                                        <PhoneInput
                                                            id='homePhone'
                                                            value={homePhone}
                                                            onChange={(value) => set_homePhone(value)}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>e.g. (212)-555-1212</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-7 medium-6 large-3'>
                                                        <label htmlFor='workPhone'>Work Phone</label>
                                                        <PhoneInput
                                                            id='workPhone'
                                                            value={workPhone}
                                                            onChange={(value) => set_workPhone(value)}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>e.g. (212)-555-1212</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-7 medium-6 large-3'>
                                                        <label htmlFor='mobilePhone'>Mobile Phone</label>
                                                        <PhoneInput
                                                            id='mobilePhone'
                                                            value={mobilePhone}
                                                            onChange={(value) => set_mobilePhone(value)}
                                                            disabled={!canEdit()}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>e.g. (212)-555-1212</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Item>
                                    <Item title={tabNames[1]}>
                                        <div style={{ minHeight: '330px' }}>
                                            <UserAgreementsTable id={id} />
                                        </div>
                                    </Item>
                                </TabPanel>
                                <div className='button-footer'>
                                    <div style={styles.buttonContainer}>
                                        <button
                                            type='submit'
                                            className='button primary'
                                            title='Save all changes'
                                            disabled={!canSave()}
                                        >
                                            Save
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                            title='Abandon changes and close this window'
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </Dialog>
    );
};

export default AdminUserModal;
